import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw, CheckCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui';

const RESEND_COOLDOWN_SECONDS = 60;

interface EmailConfirmationPendingProps {
  email: string;
  onBackToSignup?: () => void;
  onBack?: () => void;
}

export const EmailConfirmationPending: React.FC<EmailConfirmationPendingProps> = ({
  email,
  onBackToSignup,
  onBack
}) => {
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [otpCode, setOtpCode] = useState('');

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      setCooldownRemaining(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  const handleVerifyOtp = useCallback(async () => {
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'signup',
      });

      if (error) {
        let friendlyError = error.message;
        if (error.message.includes('Token has expired') || error.message.includes('otp_expired')) {
          friendlyError = 'This code has expired. Please request a new one.';
        } else if (error.message.includes('Invalid') || error.message.includes('invalid')) {
          friendlyError = 'Invalid verification code. Please check and try again.';
        }
        setError(friendlyError);
      }
      // On success, onAuthStateChange in AuthContext picks up the SIGNED_IN event automatically
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error?.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }, [email, otpCode]);

  const handleResendCode = useCallback(async () => {
    if (cooldownRemaining > 0) return;

    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        throw error;
      }

      setResendSuccess(true);
      setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error?.message || 'Failed to resend verification code');
    } finally {
      setIsResending(false);
    }
  }, [cooldownRemaining, email]);

  const isResendDisabled = isResending || cooldownRemaining > 0;

  return (
    <div className="w-full">
      {onBack && (
        <Button
          onClick={onBack}
          variant="link"
          leftIcon={<ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" />}
          className="text-[13px] mb-6"
        >
          Back
        </Button>
      )}

      <div className="text-center mb-6 sm:mb-8">
        <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <ShieldCheck className="w-7 h-7 text-neutral-600" />
        </div>
        <h2 className="text-[22px] sm:text-[24px] font-semibold text-primary-900 mb-1.5 tracking-tight">
          Verify your email
        </h2>
        <p className="text-[13px] sm:text-sm text-neutral-500 leading-relaxed">
          We've sent a 6-digit verification code to<br />
          <span className="font-medium text-neutral-700">{email}</span>
        </p>
      </div>

      {resendSuccess && (
        <div className="flex items-center gap-2.5 p-3.5 bg-green-50 border border-green-100 rounded-xl mb-4">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-[13px] text-green-700">New verification code sent!</p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-100 rounded-xl mb-4">
          <Mail className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-[13px] text-red-600">{error}</p>
        </div>
      )}

      <div className="mb-4">
        <input
          id="otp-code-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => { if (e.key === 'Enter' && otpCode.length === 6) handleVerifyOtp(); }}
          placeholder="000000"
          autoFocus
          aria-label="Verification code"
          className="w-full px-4 py-3.5 rounded-xl text-center text-2xl font-bold tracking-[0.3em]
            bg-white border border-neutral-200 text-neutral-900 placeholder-neutral-300
            focus:border-primary-400 focus:ring-2 focus:ring-primary-600/10
            transition-all duration-150 outline-none"
        />
      </div>

      <Button
        onClick={handleVerifyOtp}
        loading={isVerifying}
        disabled={isVerifying || otpCode.length !== 6}
        fullWidth
        size="lg"
        className="mb-4"
      >
        {isVerifying ? 'Verifying...' : 'Verify'}
      </Button>

      <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 mb-4">
        <p className="text-[13px] text-neutral-600 text-center">
          <span className="font-medium">Didn't receive it?</span> Check your spam folder.
        </p>
      </div>

      <div className="text-center mb-4">
        <button
          onClick={handleResendCode}
          disabled={isResendDisabled}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary-900 hover:text-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isResending ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          {isResending
            ? 'Sending...'
            : cooldownRemaining > 0
              ? `Resend code (${cooldownRemaining}s)`
              : 'Resend code'}
        </button>
      </div>

      {onBackToSignup && (
        <div className="text-center">
          <p className="text-[13px] text-neutral-500">
            Need a different email?{' '}
            <button
              onClick={onBackToSignup}
              className="font-semibold text-primary-900 hover:text-primary-700 transition-colors duration-200"
            >
              Go back
            </button>
          </p>
        </div>
      )}
    </div>
  );
};
